import { useEffect, useState } from "react";
import {
  BarChart3,
  CalendarRange,
  BookOpen,
  ChevronDown,
  ClipboardCheck,
  ExternalLink,
  Eye,
  FileText,
  GraduationCap,
  ListChecks,
  Lightbulb,
  MessageSquareWarning,
  PackageOpen,
  ReceiptText,
  Star,
  Tickets,
  TrendingUp,
  UsersRound,
  Waves,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { AppPage } from "../navigation";

type NavigationItem = {
  page: AppPage;
  label: string;
  count?: string;
  icon: LucideIcon;
  relatedPages?: readonly AppPage[];
};

type NavigationGroup = {
  id: "data" | "operations" | "knowledge" | "automation";
  label: string;
  icon: LucideIcon;
  items: readonly NavigationItem[];
};

const navigationGroups: readonly NavigationGroup[] = [
  {
    id: "data",
    label: "数据与店铺",
    icon: BarChart3,
    items: [
      { page: "dashboard", label: "全平台数据看板", count: "57店", icon: TrendingUp, relatedPages: ["tmall", "kuaishou", "pdd", "douyin"] },
      { page: "daily-agent-comparison", label: "客服每日对比", count: "每日", icon: CalendarRange },
      { page: "experience", label: "店铺后台体验分", count: "多平台", icon: Star, relatedPages: ["experience-douyin", "experience-kuaishou"] },
      { page: "patrol", label: "实时巡店预警", count: "6", icon: Eye },
    ],
  },
  {
    id: "operations",
    label: "客服运营",
    icon: UsersRound,
    items: [
      { page: "quality", label: "每日会话质检", count: "质检", icon: ListChecks },
      { page: "reviews", label: "商品差评分析", icon: MessageSquareWarning },
      { page: "product-insights", label: "商品洞察", icon: Lightbulb },
    ],
  },
  {
    id: "knowledge",
    label: "知识与培训",
    icon: GraduationCap,
    items: [
      { page: "exam", label: "新员工考试", count: "30题", icon: ClipboardCheck },
      { page: "product", label: "商品知识与话术", count: "22", icon: BookOpen },
      { page: "knowledge", label: "AI客服知识库", count: "8", icon: BookOpen },
    ],
  },
  {
    id: "automation",
    label: "业务处理",
    icon: Workflow,
    items: [
      { page: "invoice", label: "自动开票", count: "6", icon: ReceiptText },
      { page: "automation", label: "平台服务工单", count: "聚合", icon: Tickets },
      { page: "bills", label: "平台账单归集", count: "7", icon: FileText },
      { page: "erp", label: "ERP物流自动留言", count: "6", icon: PackageOpen },
    ],
  },
];

function isItemActive(item: NavigationItem, current: AppPage) {
  return item.page === current || item.relatedPages?.includes(current) === true;
}

function activeGroup(current: AppPage) {
  return navigationGroups.find((group) => group.items.some((item) => isItemActive(item, current)))?.id ?? "data";
}

export function Sidebar({
  open,
  current,
  onNavigate,
  onClose,
}: {
  open: boolean;
  current: AppPage;
  onNavigate: (page: AppPage) => void;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState<Record<NavigationGroup["id"], boolean>>(() => ({
    data: activeGroup(current) === "data",
    operations: activeGroup(current) === "operations",
    knowledge: activeGroup(current) === "knowledge",
    automation: activeGroup(current) === "automation",
  }));

  useEffect(() => {
    const group = activeGroup(current);
    setExpanded((state) => state[group] ? state : { ...state, [group]: true });
  }, [current]);

  return (
    <aside className={`sidebar ${open ? "open" : ""}`}>
      <div className="brand">
        <span className="brandMark">
          <Waves aria-hidden="true" />
        </span>
        <div>
          <strong>爱兰歌娜事业部</strong>
          <span>客服运营作业台</span>
        </div>
      </div>
      <div className="navLabel">业务导航</div>
      <nav className="moduleNav" aria-label="业务导航">
        {navigationGroups.map((group) => {
          const GroupIcon = group.icon;
          const groupOpen = expanded[group.id];
          return (
            <section className="navGroup" key={group.id}>
              <button
                type="button"
                className={`navGroupToggle ${group.items.some((item) => isItemActive(item, current)) ? "containsActive" : ""}`}
                aria-expanded={groupOpen}
                aria-controls={`nav-group-${group.id}`}
                onClick={() => setExpanded((state) => ({ ...state, [group.id]: !state[group.id] }))}
              >
                <GroupIcon aria-hidden="true" />
                <strong>{group.label}</strong>
                <small>{group.items.length}项</small>
                <ChevronDown className={groupOpen ? "expanded" : ""} aria-hidden="true" />
              </button>
              <div className="navGroupItems" id={`nav-group-${group.id}`} hidden={!groupOpen}>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.page}
                      type="button"
                      className={isItemActive(item, current) ? "active" : ""}
                      onClick={() => {
                        onNavigate(item.page);
                        onClose();
                      }}
                    >
                      <Icon aria-hidden="true" />
                      {item.label}
                      {item.count && <span>{item.count}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </nav>
      <div className="navLabel quickLabel">快捷入口</div>
      <nav>
        <a href={`/#${current}`} target="_blank" rel="noreferrer">
          <ExternalLink aria-hidden="true" />
          独立窗口打开
        </a>
      </nav>
      <div className="sidebarFooter">
        <span className="avatar">主</span>
        <div>
          <strong>客服主管</strong>
          <span>运营工作台 · 在线</span>
        </div>
      </div>
    </aside>
  );
}
